/**
 * TMS-NQL seed data:
 *   • carrier_compliance — snapshot per carrier for AEGIS compliance checks
 *   • quote_outcomes     — 90-day win/loss history for AEGIS win-probability
 *
 * Loaded by seedLoader.js for the kairos-demo tenant.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const TENANT = DEMO_TENANT_ID;
const today = new Date();
const isoPlus = (days) => new Date(today.getTime() + days * 86400000).toISOString().slice(0, 10);
const isoMinus = (days) => new Date(today.getTime() - days * 86400000).toISOString();

// Carriers we already seed via seedDataExpanded: OD, Estes, XPO, Saia, ABF plus generic
// Mix of green / amber / red so the demo shows the full spectrum.
export const SEED_CARRIER_COMPLIANCE = [
  {
    carrierId: 'carrier-od', scac: 'ODFL', carrierName: 'Old Dominion Freight Line',
    dotNumber: '48416', authorityStatus: 'active', authorityDate: '2002-03-01',
    insuranceExpiry: isoPlus(183), safetyScore: 94,
    lastChecked: isoMinus(1), fraudFlags: [], tenantBlacklisted: false,
    tenantId: TENANT,
  },
  {
    carrierId: 'carrier-estes', scac: 'EXLA', carrierName: 'Estes Express Lines',
    dotNumber: '40524', authorityStatus: 'active', authorityDate: '1999-06-14',
    insuranceExpiry: isoPlus(212), safetyScore: 91,
    lastChecked: isoMinus(1), fraudFlags: [], tenantBlacklisted: false,
    tenantId: TENANT,
  },
  {
    carrierId: 'carrier-xpo', scac: 'XPOL', carrierName: 'XPO Logistics',
    dotNumber: '80321', authorityStatus: 'active', authorityDate: '2011-09-02',
    insuranceExpiry: isoPlus(9), safetyScore: 82,
    lastChecked: isoMinus(1), fraudFlags: [], tenantBlacklisted: false,
    tenantId: TENANT,
  },
  {
    carrierId: 'carrier-saia', scac: 'SAIA', carrierName: 'Saia LTL Freight',
    dotNumber: '223670', authorityStatus: 'active', authorityDate: '2000-01-15',
    insuranceExpiry: isoPlus(145), safetyScore: 88,
    lastChecked: isoMinus(1), fraudFlags: [], tenantBlacklisted: false,
    tenantId: TENANT,
  },
  {
    carrierId: 'carrier-abf', scac: 'ABFS', carrierName: 'ABF Freight',
    dotNumber: '82866', authorityStatus: 'active', authorityDate: '1982-07-01',
    insuranceExpiry: isoPlus(301), safetyScore: 90,
    lastChecked: isoMinus(1), fraudFlags: [], tenantBlacklisted: false,
    tenantId: TENANT,
  },
  // Amber example
  {
    carrierId: 'carrier-rr', scac: 'RRCX', carrierName: 'Redwood Regional Carriers',
    dotNumber: '3412990', authorityStatus: 'active', authorityDate: '2019-04-11',
    insuranceExpiry: isoPlus(11), safetyScore: 71,
    lastChecked: isoMinus(2), fraudFlags: [], tenantBlacklisted: false,
    tenantId: TENANT,
  },
  // Red example — revoked authority
  {
    carrierId: 'carrier-gl', scac: 'GLXT', carrierName: 'GreatLakes Xpress',
    dotNumber: '2987441', authorityStatus: 'revoked', authorityDate: isoMinus(30).slice(0,10),
    insuranceExpiry: isoPlus(60), safetyScore: 48,
    lastChecked: isoMinus(3), fraudFlags: ['double-broker-suspected'], tenantBlacklisted: false,
    tenantId: TENANT,
  },
  // Red example — blacklisted
  {
    carrierId: 'carrier-bk', scac: 'BKLT', carrierName: 'Blackline Transit',
    dotNumber: '4091220', authorityStatus: 'active', authorityDate: '2022-11-02',
    insuranceExpiry: isoPlus(90), safetyScore: 65,
    lastChecked: isoMinus(2), fraudFlags: [], tenantBlacklisted: true,
    tenantId: TENANT,
  },
];

// Quote outcomes seed — build a realistic 90-day history for win probability.
// Focus on the Chicago → Romeoville lane (demo Riverside NF scenario) plus
// some other lanes so the all-lanes fallback also produces meaningful numbers.
function makeOutcome(idx, config) {
  const created = new Date(today.getTime() - (config.daysAgo || idx) * 86400000).toISOString();
  return {
    id: `qo-seed-${idx}`,
    quoteId: `Q-SEED-${1000 + idx}`,
    tenantId: TENANT,
    customerId: config.customerId,
    origin: config.origin,
    destination: config.destination,
    originRegion: config.originRegion,
    destinationRegion: config.destinationRegion,
    mode: config.mode || 'FTL',
    rate: config.rate,
    outcome: config.outcome,
    lostReason: config.lostReason || null,
    createdAt: created,
  };
}

const SCENARIOS = [
  // Chicago, IL → Romeoville, IL (Riverside NF) — 24 quotes spread over 90 days
  // Wins cluster around $1,380; losses at $1,480+
  { count: 6, customerId: 'cust-riverside', origin: 'Chicago, IL', destination: 'Romeoville, IL',
    originRegion: 'IL', destinationRegion: 'IL', mode: 'Reefer', rate: 1380, outcome: 'won' },
  { count: 4, customerId: 'cust-riverside', origin: 'Chicago, IL', destination: 'Romeoville, IL',
    originRegion: 'IL', destinationRegion: 'IL', mode: 'Reefer', rate: 1405, outcome: 'won' },
  { count: 3, customerId: 'cust-riverside', origin: 'Chicago, IL', destination: 'Romeoville, IL',
    originRegion: 'IL', destinationRegion: 'IL', mode: 'Reefer', rate: 1480, outcome: 'lost', lostReason: 'rate_too_high' },
  { count: 4, customerId: 'cust-riverside', origin: 'Chicago, IL', destination: 'Romeoville, IL',
    originRegion: 'IL', destinationRegion: 'IL', mode: 'Reefer', rate: 1500, outcome: 'lost', lostReason: 'rate_too_high' },
  { count: 3, customerId: 'cust-riverside', origin: 'Chicago, IL', destination: 'Romeoville, IL',
    originRegion: 'IL', destinationRegion: 'IL', mode: 'Reefer', rate: 1350, outcome: 'won' },
  { count: 2, customerId: 'cust-riverside', origin: 'Chicago, IL', destination: 'Romeoville, IL',
    originRegion: 'IL', destinationRegion: 'IL', mode: 'Reefer', rate: 1620, outcome: 'lost', lostReason: 'went_with_competitor' },

  // TX → CA — 12 quotes
  { count: 4, customerId: 'cust-watchmen', origin: 'Houston, TX', destination: 'Los Angeles, CA',
    originRegion: 'TX', destinationRegion: 'CA', mode: 'FTL', rate: 2850, outcome: 'won' },
  { count: 3, customerId: 'cust-watchmen', origin: 'Houston, TX', destination: 'Los Angeles, CA',
    originRegion: 'TX', destinationRegion: 'CA', mode: 'FTL', rate: 3100, outcome: 'lost', lostReason: 'timing' },
  { count: 5, customerId: 'cust-watchmen', origin: 'Houston, TX', destination: 'Los Angeles, CA',
    originRegion: 'TX', destinationRegion: 'CA', mode: 'FTL', rate: 2950, outcome: 'won' },

  // GA → FL — 8 quotes
  { count: 3, customerId: 'cust-sunbelt', origin: 'Atlanta, GA', destination: 'Miami, FL',
    originRegion: 'GA', destinationRegion: 'FL', mode: 'FTL', rate: 1250, outcome: 'won' },
  { count: 3, customerId: 'cust-sunbelt', origin: 'Atlanta, GA', destination: 'Miami, FL',
    originRegion: 'GA', destinationRegion: 'FL', mode: 'FTL', rate: 1320, outcome: 'lost', lostReason: 'went_with_competitor' },
  { count: 2, customerId: 'cust-sunbelt', origin: 'Atlanta, GA', destination: 'Miami, FL',
    originRegion: 'GA', destinationRegion: 'FL', mode: 'FTL', rate: 1220, outcome: 'won' },
];

export const SEED_QUOTE_OUTCOMES = (() => {
  const out = [];
  let idx = 0;
  SCENARIOS.forEach(s => {
    for (let i = 0; i < s.count; i++) {
      const daysAgo = Math.floor(Math.random() * 85) + 2; // spread over 90 days
      out.push(makeOutcome(idx++, { ...s, daysAgo }));
    }
  });
  return out;
})();

// Quote templates for the New Quote "Template Lane" mode.
export const SEED_QUOTE_TEMPLATES = [
  {
    id: 'tpl-riverside-daily',
    tenantId: TENANT,
    customerId: 'cust-riverside',
    customerName: 'Riverside Natural Foods',
    lane: 'Chicago, IL → Romeoville, IL',
    origin: 'Chicago, IL',
    destination: 'Romeoville, IL',
    mode: 'Reefer',
    weight_lbs: 42000,
    commodity: 'Frozen bakery',
    lastRate: 1380,
    lastCarrier: 'XPO Logistics',
    usesCount: 47,
    updatedAt: isoMinus(2),
  },
  {
    id: 'tpl-watchmen-tx-ca',
    tenantId: TENANT,
    customerId: 'cust-watchmen',
    customerName: 'Watchmen Logistics',
    lane: 'Houston, TX → Los Angeles, CA',
    origin: 'Houston, TX',
    destination: 'Los Angeles, CA',
    mode: 'FTL',
    weight_lbs: 44500,
    commodity: 'Industrial parts',
    lastRate: 2850,
    lastCarrier: 'Old Dominion',
    usesCount: 23,
    updatedAt: isoMinus(4),
  },
  {
    id: 'tpl-sunbelt-ga-fl',
    tenantId: TENANT,
    customerId: 'cust-sunbelt',
    customerName: 'Sunbelt Produce',
    lane: 'Atlanta, GA → Miami, FL',
    origin: 'Atlanta, GA',
    destination: 'Miami, FL',
    mode: 'FTL',
    weight_lbs: 38000,
    commodity: 'Produce',
    lastRate: 1250,
    lastCarrier: 'Saia LTL',
    usesCount: 18,
    updatedAt: isoMinus(7),
  },
];

export const NQL_SEED_SUMMARY = {
  carrier_compliance: SEED_CARRIER_COMPLIANCE.length,
  quote_outcomes: SEED_QUOTE_OUTCOMES.length,
  quote_templates: SEED_QUOTE_TEMPLATES.length,
};
